import { HttpBackend } from '@angular/common/http';
import { HttpClientMeraki, MERAKI_API_GATEWAY, MERAKI_CACHE_LB } from '../meraki-nexus.providers';
import { MerakiAuthService } from '../meraki-auth.service';
import { AngularFirestore } from '@angular/fire/firestore';
import { Inject, Injectable } from '@angular/core';
import { VisitReasonQuestionViewModel, VisitReasonViewModel } from '../meraki-client.service';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, first, map, switchMap, take, tap } from 'rxjs/operators';
import {
  BranchProviderInfoVo,
  MedicalInsurerVo,
  PatientVo,
  ProviderBranchConfigurationVo,
  SpecialityRuleVo,
  SupportingProviderVo,
  Symptom,
  SymptomQuestion,
} from '../api-client.service';
import { Address, Country } from '../meraki-models/general.models';
import { Insurer } from '../meraki-models/insurer.models';
import { PracticeProvider } from '../meraki-models/provider.models';
import { Branch, Practice } from '../meraki-models/practice.models';
import * as guidByString from 'uuid-by-string';
import { PpeCode, NexBaseResponse, NexResponse } from '../meraki-models/meraki-models';
import firebase from 'firebase/app';
import 'firebase/firestore';
import Timestamp = firebase.firestore.Timestamp;
import { DischemPharmacy } from '../models/dischem.model';
import { SuggestedMedsResponse } from '../meraki-models/suggestive-medicine.models';
import { CustomChargeCodes, CustomChargeCodeType } from '../meraki-models/custom-charge-codes.models';
import { HealthIdSearchRequest, HealthIdSearchResponse } from '../health-id.service';
import * as moment from 'moment';
import { PathologyLabLookup } from '../models/lab-mnemonic-mapping.model';

@Injectable({
  providedIn: 'root',
})
export class MerakiLookupService {
  private http: HttpClientMeraki;
  private baseUrl: string;
  private loadBalancerUrl: string;

  constructor(
    private httpHandler: HttpBackend, // Bypass Auth interceptor since it overrides with florence auth token.
    @Inject(HttpClientMeraki) http: HttpClientMeraki,
    @Inject(MERAKI_API_GATEWAY) baseUrl: string,
    @Inject(MERAKI_CACHE_LB) cacheLbUrl: string,
    private authService: MerakiAuthService,
    private firestore: AngularFirestore
  ) {
    this.http = http;
    //this.http = new HttpClient(httpHandler);

    this.baseUrl = baseUrl ? baseUrl : '';
    this.loadBalancerUrl = cacheLbUrl;
  }

  getCareEventNotesLookup(): Observable<any> {
    return this.firestore
      .collection('Configuration')
      .doc('CareEventNotesLookup')
      .get()
      .pipe(
        tap(doc => console.log('doc', doc.data())),
        map(doc => doc.data()),
        catchError(error => {
          console.error(error);
          return of({});
        })
      );
  }

  getDischemPharmacies(): Observable<DischemPharmacy[]> {
    return this.firestore
      .collection('Configuration')
      .doc('DischemPharmacies')
      .valueChanges()
      .pipe(map((data: any) => data.pharmacies));
  }

  getDefaultConfigurationByType(): Observable<any> {
    return this.firestore
      .collection('Configuration')
      .doc('AppointmentType')
      .get()
      .pipe(
        map(doc => doc.data()),
        first()
      );
  }

  getVisitReasons(): Observable<VisitReasonViewModel[]> {
    return this.firestore
      .collection('ClinicalVisitReason')
      .doc('VisitReasons')
      .valueChanges()
      .pipe(
        take(1),
        map(data => Object.values(data))
      );
  }

  getVisitReasonsByPractice(bpn: string): Observable<VisitReasonViewModel[]> {
    return this.firestore
      .collection(`Practice/${bpn}/VisitReason`)
      .doc('VisitReasons')
      .valueChanges()
      .pipe(
        take(1),
        map(data => Object.values(data || []))
      );
  }

  getCustomChargeCodes(bpn: string, type: CustomChargeCodeType): Observable<CustomChargeCodes[]> {
    return this.firestore
      .collection(`Practice/${bpn}/CustomChargeCode`, ref => ref.where('Type', '==', type).where('Status', '==', 'Active'))
      .valueChanges()
      .pipe(
        take(1),
        map(data => data.map((item: any) => ({ ...item }) as CustomChargeCodes))
      );
  }

  getVisitReasonQuestions(id: string): Observable<VisitReasonQuestionViewModel[]> {
    return this.firestore
      .collection('ClinicalVisitReason')
      .doc(id)
      .valueChanges()
      .pipe(
        take(1),
        map(data => Object?.values(data || []))
      );
  }

  getCountries(): Observable<Country[]> {
    return this.firestore
      .collection('Countries')
      .valueChanges<string>({ idField: 'Alpha2' })
      .pipe(
        take(1),
        catchError(err => {
          console.error('error', err);
          return of([]);
        })
      );
  }

  getVisitReasonQuestionsByPractice(bpn: string, id: string): Observable<VisitReasonQuestionViewModel[]> {
    return this.firestore
      .collection(`Practice/${bpn}/VisitReason`)
      .doc(id)
      .valueChanges()
      .pipe(
        take(1),
        map(data => Object?.values(data || []))
      );
  }

  upsertVisitReasonByPractice(bpn: string, visitReason: Symptom): Observable<void> {
    return from(
      this.firestore
        .collection(`Practice/${bpn}/VisitReason`)
        .doc('VisitReasons')
        .set(
          {
            [visitReason.SymptomId]: visitReason,
          },
          { merge: true }
        )
    );
  }

  /**
   * Removes a visit reason entry from Practice/{bpn}/VisitReason/VisitReasons (metadata only).
   * Note: does NOT delete the questions JSON document at Practice/{bpn}/VisitReason/{symptomId}.
   */
  removeVisitReasonByPractice(bpn: string, symptomId: string): Observable<void> {
    return from(
      this.firestore
        .collection(`Practice/${bpn}/VisitReason`)
        .doc('VisitReasons')
        .update({
          [symptomId]: firebase.firestore.FieldValue.delete(),
        })
    );
  }

  upsertVisitReasonQuestionsByPractice(bpn: string, symptomId: string, questions: SymptomQuestion[]): Observable<void> {
    const keyedQuestions = (questions || []).reduce((acc, question, index) => {
      const key = question?.QuestionKey || `Question_${index + 1}`;
      return { ...acc, [key]: question };
    }, {});

    return from(this.firestore.collection(`Practice/${bpn}/VisitReason`).doc(symptomId).set(keyedQuestions));
  }

  getAllMedicalInsurers() {
    return this.firestore
      .collection('Configuration')
      .doc('SchemeLookup')
      .valueChanges()
      .pipe(
        first(),
        map((item: any) => item.MedicalInsurers),
        map((insurers: { [key: string]: any }) =>
          Object.keys(insurers)
            .reduce((result, insurerCode) => {
              result.push({ ...insurers[insurerCode], Code: insurerCode, Id: insurerCode } as Insurer);
              return result;
            }, [])
            .sort((i1, i2) => i1?.Name?.localeCompare(i2.Name))
        ),
        map((items: Insurer[]) =>
          items.map(
            item =>
              ({
                Name: item.Name,
                Email: item.Email,
                AccountNo: item.Code,
                Id: item.Code,
                InsurerXRef: item.Id,
                Phone1: item.Phone,
              }) as MedicalInsurerVo
          )
        )
      );
  }

  getAllMedicalAids() {
    return (
      this.firestore
        .collection('SchemePlanOption')
        //    .valueChanges<string>({ idField: 'Id' })
        .valueChanges()
        .pipe(take(1))
    );
  }

  getSupportingProviders(bpn: string): Observable<SupportingProviderVo[]> {
    return this.firestore
      .collection('Practice')
      .doc(bpn)
      .collection('Provider')
      .valueChanges()
      .pipe(
        first(),
        map((providers: PracticeProvider[]) =>
          providers
            // todo check how to get assisting/referring provider
            // .filter(p => p.Type == ProviderType.EXTERNAL)
            .map(
              p =>
                ({
                  Name: p.Name,
                  FriendlyName: `${p.Title ?? ''} ${p.Name} ${p.Surname}`.trim(),
                  HpcsaNumber: p.HPCSANumber,
                  Surname: p.Surname,
                  Title: p.Title,
                  TreatingPracticeNumber: p.TreatingPracticeNumber,
                  Type: p.Type, // todo check what types exist
                  SupportingProviderXRef: p.HPCSANumber,
                }) as SupportingProviderVo
            )
        )
      );
  }

  getPracticeProvider(bpn: string, hpcsaNumber: string): Observable<PracticeProvider> {
    return this.firestore.collection('Practice').doc(bpn).collection('Provider').doc(hpcsaNumber).valueChanges().pipe(first());
  }

  addressToString(address: Address): string {
    return `${address.Line1}\n${address.Line2}\n${address.Line3}\n${address.Code}`;
  }

  getPracticeBranches(bpn: string): Observable<ProviderBranchConfigurationVo[]> {
    return forkJoin([
      this.firestore.collection('Practice').doc(bpn).valueChanges<string>({ idField: 'Id' }).pipe(first()),
      this.firestore.collection('Practice').doc(bpn).collection('Provider').valueChanges<string>({ idField: 'Id' }).pipe(first()),
    ]).pipe(
      map(([practice, providers]: [Practice, PracticeProvider[]]) =>
        !practice.IsMultiBranch
          ? // for single branch, just load the main branch
            (practice.Branches?.filter(s => s.Active && s.IsMainBranch).map((b: Branch) => this.toProviderBranch(b, providers)) ?? [])
          : practice.Branches.map((b: Branch) => this.toProviderBranch(b, providers))
      )
    );
  }

  private toProviderBranch(b: Branch, providers: PracticeProvider[]): ProviderBranchConfigurationVo {
    return {
      BranchName: b.Name,
      BranchId: guidByString(b.Name, 5),
      BranchXRef: b.Name,
      IsMainBranch: b.IsMainBranch,
      Enabled: b.Active,
      PhoneNumber: b.ContactDetails.OfficeNo,
      PhysicalAddress: this.addressToString(b.PhysicalAddress),
      PostalAddress: this.addressToString(b.PostalAddress),
      ProviderInfos: providers
        ?.flatMap(d =>
          d.Common?.AssignedBranches.filter(provider => provider.BranchName == b.Name).map(
            s =>
              ({
                IsDefaultBranch: s.IsDefault,
                DispensingLicenseNumber: s.DispensingLicenceNo,
                IsDispensing: s.IsDispensing,
                ProviderXRef: d.Id,
              }) as BranchProviderInfoVo
          )
        )
        .filter(s => !!s),
    } as ProviderBranchConfigurationVo;
  }

  getSpecialityRule(practiceId: string, code: string): Observable<SpecialityRuleVo> {
    return this.firestore.collection('SpecialityRule').doc(code).valueChanges().pipe(first());
  }

  getPpeCodes(optionCode: string, specialityCode: string): Observable<PpeCode> {
    return this.firestore
      .collection('PPECode')
      .doc(`${optionCode}_${specialityCode.replace(/^0+/, '')}`)
      .valueChanges()
      .pipe(first());
  }

  getFeatureAddOnConfig(id: string): Observable<any> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http
          .get<any>(`${this.baseUrl}/v1/clinical/config/getFeatureAddOnConfiguration?id=${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .pipe(
            map(data => ({
              ...data.config,
              DateNow: new Date(data.config.DateNow),
              BeginDate: new Timestamp(data.config.BeginDate._seconds, data.config.BeginDate._nanoseconds).toDate(),
              EndDate: new Timestamp(data.config.EndDate._seconds, data.config.EndDate._nanoseconds).toDate(),
            }))
          )
      )
    );
  }

  loadSuggestiveMeds(doctorId: string, icdCode: string[], patient: PatientVo): Observable<SuggestedMedsResponse> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.post<NexResponse<SuggestedMedsResponse>>(
          `${this.loadBalancerUrl}/v1/clinical/cli/suggestiveMedicineV2`,
          // `https://europe-west1-healthbridge-ba575.cloudfunctions.net/suggestive_meds_prediction_model_1`,
          {
            icd10_code_list: icdCode,
            doctor_id: doctorId,
            patient_birth_date: moment(patient.PatientDetails?.DateOfBirth).format('YYYY-MM-DD'),
            patient_gender: patient.PatientDetails.Gender,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
      ),
      map((response: NexResponse<SuggestedMedsResponse>) => response.data.payload)
    );
  }

  getHealthIdLinkForPatient(request: HealthIdSearchRequest): Observable<HealthIdSearchResponse> {
    return this.authService.getFirebaseToken().pipe(
      switchMap(token =>
        this.http.post<NexResponse<HealthIdSearchResponse>>(`${this.loadBalancerUrl}/v1/clinical/cli/healthIdPatientSearch`, request, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ),
      map((response: NexResponse<HealthIdSearchResponse>) => response.data.payload),
      catchError(err => {
        console.error('Error fetching health ID link for patient:', err);
        return of({
          overview: '',
          medicalHistory: '',
          careProgramme: '',
          cib: '',
          keycare: '',
          status: err,
          message: 'Error fetching health ID link for patient',
        } as HealthIdSearchResponse);
      })
    );
  }

  isPathologyMessengerEnabled(practiceId: string): Observable<boolean> {
    if (!practiceId) {
      return of(false);
    }
    return this.firestore
      .collection('ClinicalPractice')
      .doc(practiceId)
      .collection('Settings')
      .doc(practiceId)
      .get()
      .pipe(
        map(doc => {
          if (!doc.exists) {
            return false;
          }
          const settings = doc.data();
          return settings?.PathologyMessenger?.Enabled === true;
        }),
        catchError(() => of(false)),
        first()
      );
  }

  isXCONSEnabled(practiceId: string): Observable<boolean> {
    if (!practiceId) {
      return of(false);
    }
    return this.firestore
      .collection('ClinicalPractice')
      .doc(practiceId)
      .collection('Settings')
      .doc(practiceId)
      .get()
      .pipe(
        map(doc => {
          if (!doc.exists) {
            return false;
          }
          const settings = doc.data();
          return settings?.XCONS?.Enabled === true;
        }),
        catchError(() => of(false)),
        first()
      );
  }

  enablePathologyMessenger(practiceId: string): Observable<{ Success: boolean; Error?: any }> {
    return from(
      this.firestore
        .collection('ClinicalPractice')
        .doc(practiceId)
        .collection('Settings')
        .doc(practiceId)
        .set(
          {
            PathologyMessenger: {
              Enabled: true,
            },
          },
          { merge: true }
        )
    ).pipe(
      map(() => ({ Success: true })),
      catchError(error => {
        console.error('Error enabling PathologyMessenger:', error);
        return of({ Success: false, Error: error });
      })
    );
  }

  /**
   * Upload pathology lab lookup data to Firestore
   * Stores all labs in a single document at Configuration/PathologyLabs with Labs array field
   * @param labs Array of lab lookup records
   * Note: This is a one-time seed operation, typically run from console or seed script
   */
  uploadPathologyLabs(labs: PathologyLabLookup[]): Observable<any> {
    return from(this.firestore.collection('Configuration').doc('PathologyLabs').set({ Labs: labs }));
  }

  /**
   * Get a specific pathology lab by ID
   * Reads from Configuration/PathologyLabs document and filters Labs array
   * @param labId Lab identifier (e.g., "lancet-laboratory")
   */
  getPathologyLabLookup(labId: string): Observable<PathologyLabLookup> {
    return this.firestore
      .collection('Configuration')
      .doc<{ Labs: PathologyLabLookup[] }>('PathologyLabs')
      .valueChanges()
      .pipe(
        first(),
        map(doc => doc?.Labs?.find(lab => lab.labId === labId))
      );
  }

  /**
   * Get all pathology labs from lookup document
   * Reads Labs array field from Configuration/PathologyLabs document
   * @returns Observable of all available labs with names, sender codes, and icons
   */
  getAllPathologyLabs(): Observable<PathologyLabLookup[]> {
    return this.firestore
      .collection('Configuration')
      .doc<{ Labs: PathologyLabLookup[] }>('PathologyLabs')
      .valueChanges()
      .pipe(
        take(1),
        map(doc => doc?.Labs || [])
      );
  }
}
